import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { ItemResponse, ItemRequest } from '@models/item';
import { Paginated } from '@models/paginated';
import '@extensions/string';
import { ThunkCallback } from '../model/thunk-callback';
import { addNotification } from '../notification';
import { translate } from '@components/i18n';
import { toQueryParameters } from '@extensions/object';
import { PaginatedResponseApi } from '@models/order/items/response';
import { exportReport } from '@utils/file';
import { ITEM_STATUS } from 'src/enums/item-status';

export const fetchItems = createAsyncThunk<
    Paginated<ItemResponse>,
    ItemRequest
>('items/list', async (request, thunkAPI) => {
    const params = toQueryParameters({
        page: request.current - 1,
        size: request.pageSize < 10 ? 10 : request.pageSize,
        sort: request.sort || 'descricao,asc',
        query: request.query || '',
        status: request.status
    });
    try {
        const response = await axios.get<PaginatedResponseApi<ItemResponse>>(
            `/item/filter?${params}`,
        );

        const { data, status } = response;

        if (!data) {
            return {
                items: [],
                current: 0,
                total: 0,
                pageSize: 0,
            };
        } else {
            if (status === 200) {
                return {
                    items: data.content,
                    current: request.current,
                    total: response.data.totalElements,
                    pageSize: response.data.numberOfElements,
                    totalPages: response.data.totalPages,
                };
            } else {
                return thunkAPI.rejectWithValue('');
            }
        }
    } catch (e) {
        // TODO: here will be fetch's actions
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

export const createItem = createAsyncThunk<
    ItemResponse,
    ThunkCallback<ItemResponse>
>('items/create', async (request, thunkAPI) => {
    try {
        const response = await axios.post<ItemResponse>('/item', request.data);

        const { data, status } = response;

        if (status === 201 && data) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'success',
                    message: translate('general.updated'),
                    title: translate('general.success'),
                    notificationKey: request.notificationKey,
                    callback: request.onSuccess,
                }),
            );

            return data;
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        // TODO: here will be fetch's actions
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

export const updateItem = createAsyncThunk<
    ItemResponse,
    ThunkCallback<ItemResponse>
>('items/update', async (request, thunkAPI) => {
    try {
        const response = await axios.put<ItemResponse>(
            '/item/' + request.data.id,
            request.data,
        );

        const { data, status } = response;

        if (status === 200) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'success',
                    message: translate('general.itemUpdated'),
                    title: translate('general.success'),
                    notificationKey: request.notificationKey,
                    callback: request.onSuccess,
                }),
            );

            return data;
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

export const removeItem = createAsyncThunk<number, number>(
    'items/update',
    async (itemId, thunkAPI) => {
        try {
            const response = await axios.delete('/item/' + itemId);
            if (response.status === 200) {
                thunkAPI.dispatch(removeItemState(itemId));
                return Promise.resolve(itemId);
            }
            return 0;
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;
            return thunkAPI.rejectWithValue(message);
        }
    },
);

interface ExportItemsParams {
    query: string;
    status: string;
}

export const exportItems = createAsyncThunk<void, ExportItemsParams | void>('items/export', async (request, thunkAPI) => {
    try {
        const response = await axios.get(`/item/export`, { params: request, responseType: 'blob' });

        const { data, status } = response;

        if (status === 200) {
            exportReport(data, `${translate('general.exportItems')}.xlsx`);
        } else {
            return thunkAPI.rejectWithValue('');
        }
    } catch (e) {
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

interface IUItemsState {
    list: ItemResponse[];
    isFetching: boolean;
    isSuccess: boolean;
    request: ItemRequest;
    isError: boolean;
    errorMessage?: string;
    search: {
        query: string;
        status?: ITEM_STATUS,
    };
}

const initialState: IUItemsState = {
    list: [],
    isFetching: false,
    isSuccess: false,
    request: {
        pageSize: 10,
        current: 1,
        total: 0,
        query: '',
        sort: 'descricao,asc',
        totalPages: 0,
    },
    isError: false,
    errorMessage: '',
    search: {
        query: '',
    },
};

const itemsSlice = createSlice({
    name: 'itemsSlice',
    initialState,
    reducers: {
        clearState: (state) => {
            state = { ...initialState };
            return state;
        },
        setSearch: (state, action: PayloadAction<IUItemsState['search']>) => {
            state.search = action.payload;
            return state;
        },
        removeItemState: (state, action: PayloadAction<number>) => {
            state.list = state.list.filter((u) => u.id !== action.payload);
            return state;
        },
    },
    extraReducers: {
        [updateItem.fulfilled.toString()]: (
            state,
            action: PayloadAction<ItemResponse>,
        ) => {
            state.list = state.list.map((u) =>
                u.id === action.payload.id ? action.payload : u,
            );
            return state;
        },

        [fetchItems.pending.toString()]: (state) => {
            state.isFetching = true;
            return state;
        },
        [fetchItems.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
            return state;
        },
        [fetchItems.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<Paginated<ItemResponse>>,
        ) => {
            state.isFetching = false;
            state.isSuccess = true;
            state.request = {
                ...state.request,
                total: payload.total,
                pageSize: payload.pageSize,
                current: payload.current,
                totalPages: payload.totalPages,
            };
            state.list = payload.items;
            return state;
        },
    },
});

export const itemSelector = (state: RootState): IUItemsState => state.items;

export const allItems = ({ items }: RootState): ItemResponse[] => items.list;

export const { clearState, setSearch, removeItemState } = itemsSlice.actions;

export default itemsSlice.reducer;
